/** Recursively unwrap an MCP tool result down to the payload object carrying
 *  the given keys.
 *
 *  Real WS tool outputs do NOT arrive as the bare JSON the tool returned — they
 *  come wrapped, sometimes several layers at once: a JSON string, a content-block
 *  array (`{content:[{type:'text', text:'{json}'}]}`), or an `{output}`/`{result}`
 *  envelope. Shallow `JSON.parse(raw)` therefore reads an object with none of the
 *  expected keys and every field silently degrades to its empty state — which is
 *  exactly how the performance card rendered "—" tiles while the tool had returned
 *  a full template-A payload (finding B, 2026-08-22). The older cards already knew
 *  this (BrandAnalysisProposalCard, extractSelectionBrief); this is that same visit
 *  pattern, shared.
 */
export function unwrapToolPayload(
  raw: unknown,
  hasAnyKey: readonly string[],
  depth = 0,
): Record<string, unknown> | null {
  if (raw == null || depth > 6) return null;
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (!t.startsWith('{') && !t.startsWith('[')) return null;
    try {
      return unwrapToolPayload(JSON.parse(t), hasAnyKey, depth + 1);
    } catch {
      return null;
    }
  }
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const found = unwrapToolPayload(item, hasAnyKey, depth + 1);
      if (found) return found;
    }
    return null;
  }
  if (typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (hasAnyKey.some(k => k in obj)) return obj;
  // Envelope keys seen on the wire, in the order they nest.
  for (const k of ['content', 'output', 'result', 'text', 'data'] as const) {
    if (k in obj) {
      const found = unwrapToolPayload(obj[k], hasAnyKey, depth + 1);
      if (found) return found;
    }
  }
  return null;
}
